// Discard a change set that has not been applied: a required reason, and the
// option to remove the uploaded document behind it when nothing else
// references it. The server answers both questions before the reviewer
// commits to anything (PreviewDiscardChangeSet), and again on the write.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCheckboxField, SpecraFormField, SpecraInlineBanner, SpecraOverlayDialog, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { toast } from 'sonner';
import { useDiscardChangeSet, useDiscardPreview } from '../../hooks/discardChangeSetHooks';
export const DISCARD_CANNOT_UNDO = 'Discarding removes this change set from review for good: none of its changes will be applied and its open questions are closed. This can’t be undone. To try again, upload the document again.';
export const NO_UPLOADED_DOCUMENT = 'This change set wasn’t created from an uploaded document.';
// One row per thing still citing the document, keyed by the record it names:
// two sets with the same title and state are two records, never one sentence
// twice. A server without the records still sends the sentences.
export function referenceRows(p) {
    const records = p.documentReferenceRecords ?? [];
    if (records.length > 0) {
        return records.map((r, i)=>({
                key: r.recordId || `reference-${i}`,
                recordId: r.recordId,
                description: r.description
            }));
    }
    return p.documentReferences.map((description, i)=>({
            key: `reference-${i}`,
            recordId: '',
            description
        }));
}
export function DiscardChangeSetDialog({ changeSetId, label, open, onOpenChange, onDiscarded }) {
    const preview = useDiscardPreview(changeSetId, open);
    const discard = useDiscardChangeSet();
    const [reason, setReason] = useState('');
    const [removeDocument, setRemoveDocument] = useState(false);
    const p = preview.data;
    const canRemove = !!p?.documentRemovable;
    const refusal = p && !p.discardable ? p.notDiscardableReason : '';
    const references = p ? referenceRows(p) : [];
    const onConfirm = async ()=>{
        try {
            const resp = await discard.mutateAsync({
                changeSetId,
                reason: reason.trim(),
                removeDocument: canRemove && removeDocument
            });
            toast.success(resp.documentRemoved ? `${label} and its document were discarded` : `${label} was discarded`);
        } catch  {
            // useDiscardChangeSet's onError says why; the dialog stays open to retry.
            return;
        }
        onOpenChange(false);
        onDiscarded?.();
    };
    let documentNote = NO_UPLOADED_DOCUMENT;
    if (p?.hasDocument) {
        documentNote = canRemove ? 'The uploaded document is removed from the program’s documents and project files too.' : 'The document can’t be removed because it is still referenced.';
    }
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: `Discard ${label}?`,
        description: DISCARD_CANNOT_UNDO,
        testId: Testids.discardChangeSet.dialog,
        size: "md",
        footerProps: {
            cancelLabel: 'Cancel',
            onCancel: ()=>onOpenChange(false),
            confirmLabel: 'Discard',
            onConfirm: ()=>void onConfirm(),
            confirmDisabled: !reason.trim() || !p?.discardable,
            loading: discard.isPending,
            loadingLabel: 'Discarding…',
            confirmProps: {
                variant: 'danger',
                'data-testid': Testids.discardChangeSet.confirmButton
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                preview.isLoading && /*#__PURE__*/ _jsx(SpecraText, {
                    size: "caption",
                    tone: "muted",
                    children: "Checking whether this change set can be discarded…"
                }),
                preview.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    children: "Couldn’t check whether this change set can be discarded. Close this and try again."
                }),
                refusal && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    "data-testid": Testids.discardChangeSet.notDiscardable,
                    children: refusal
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Reason",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        placeholder: "Why is this change set being discarded?",
                        disabled: !!refusal,
                        "data-testid": Testids.discardChangeSet.reasonInput
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                    label: "Also remove the uploaded document",
                    description: documentNote,
                    controlProps: {
                        checked: canRemove && removeDocument,
                        onCheckedChange: (v)=>setRemoveDocument(v === true),
                        disabled: !canRemove || !!refusal,
                        'aria-label': 'Also remove the uploaded document'
                    },
                    "data-testid": Testids.discardChangeSet.removeDocumentCheckbox,
                    "data-disabled": !canRemove || undefined
                }),
                p?.hasDocument && !canRemove && references.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.discardChangeSet.removeDocumentBlocked,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "caption",
                            tone: "muted",
                            children: "Still referenced by:"
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: "list-disc pl-5 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: references.map((r)=>/*#__PURE__*/ _jsx("li", {
                                    "data-record-id": r.recordId || undefined,
                                    children: r.description
                                }, r.key))
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "caption",
                            tone: "muted",
                            children: "You can still discard the change set; the document stays."
                        })
                    ]
                })
            ]
        })
    });
}
